
import React, { useState, useEffect } from 'react';
import { Coupon } from '../types';
import CouponTable from './CouponTable';
import CreateCouponModal from './CreateCouponModal';
import { api } from '../services/api';

const Coupons: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const data = await api.coupons.getAll();
      if (data && Array.isArray(data)) {
        setCoupons(data);
      }
    } catch (e) {
      console.error("Coupon fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopyFeedback(code);
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  const handleSaveCoupon = async (coupon: Coupon) => {
    await api.coupons.create(coupon);
    setIsCreating(false);
    await fetchCoupons();
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Discounts & Promotions</h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg">Manage your promotional campaigns and coupon codes.</p>
        </div>
        <button onClick={() => setIsCreating(true)} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 active:scale-95">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Create Discount
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm relative overflow-hidden group">
          <div className="relative z-10">
            <p className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Active Now</p>
            <h3 className="text-4xl font-black text-slate-900 dark:text-white">{coupons.filter(c => c.status === 'Active').length}</h3>
          </div>
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 dark:bg-emerald-900/20 rounded-full -mr-10 -mt-10 group-hover:scale-110 transition-transform"></div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm relative overflow-hidden group">
          <div className="relative z-10">
            <p className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Scheduled</p>
            <h3 className="text-4xl font-black text-slate-900 dark:text-white">{coupons.filter(c => c.status === 'Scheduled').length}</h3>
          </div>
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 dark:bg-blue-900/20 rounded-full -mr-10 -mt-10 group-hover:scale-110 transition-transform"></div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm relative overflow-hidden group">
          <div className="relative z-10">
            <p className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Total Impact</p>
            <h3 className="text-4xl font-black text-indigo-600">-$12.4k</h3>
          </div>
          <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 dark:bg-indigo-900/20 rounded-full -mr-10 -mt-10 group-hover:scale-110 transition-transform"></div>
        </div>
      </div>

      {loading && coupons.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm">
           <svg className="animate-spin h-8 w-8 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Compiling Redemption Ledger...</p>
        </div>
      ) : (
        <CouponTable coupons={coupons} onCopy={handleCopy} copyFeedback={copyFeedback} />
      )}
      
      {isCreating && <CreateCouponModal onClose={() => setIsCreating(false)} onSave={handleSaveCoupon} />}
    </div>
  );
};

export default Coupons;
